import {
  ArrowLeftRight,
  Download,
  Pencil,
  Redo2,
  Sparkles,
  Trash2,
  Undo2,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RouteControlsProps {
  // Active planning mode, or null when routing is off.
  planMode: "manual" | "suggested" | null;
  canUndo: boolean;
  canRedo: boolean;
  hasWaypoints: boolean;
  hasRoute: boolean;
  onSelectSuggested: () => void;
  onSelectManual: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  onReverse: () => void;
  onExportGPX: () => void;
}

// Two distinct entry points: "Suggested" (start/end fields + generate, in the
// side panel) and "Manual" (click the map to build a route by hand). Clicking
// the active mode again cancels it. Manual mode reveals its edit actions.
export const RouteControls = ({
  planMode,
  canUndo,
  canRedo,
  hasWaypoints,
  hasRoute,
  onSelectSuggested,
  onSelectManual,
  onUndo,
  onRedo,
  onClear,
  onReverse,
  onExportGPX,
}: RouteControlsProps) => {
  const modeButton = (
    active: boolean,
    onClick: () => void,
    Icon: typeof Pencil,
    label: string,
  ) => (
    <button
      type="button"
      onClick={onClick}
      title={active ? `Cancel ${label.toLowerCase()}` : label}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md shadow transition-colors",
        active
          ? "bg-stone-800 text-white"
          : "bg-white text-stone-700 hover:bg-stone-50",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="absolute right-4 top-4 flex flex-col items-end gap-2">
      <div className="flex gap-2">
        {modeButton(planMode === "manual", onSelectManual, Pencil, "Manual")}
        {modeButton(
          planMode === "suggested",
          onSelectSuggested,
          Sparkles,
          "Suggested",
        )}
      </div>

      <AnimatePresence>
        {planMode === "manual" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.12 }}
            className="flex overflow-hidden rounded-md bg-white shadow"
          >
            <Button
              variant="ghost"
              className="w-[50px] rounded-none"
              size="icon"
              onClick={onUndo}
              disabled={!canUndo}
              title="Undo"
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              className="w-[50px] rounded-none"
              size="icon"
              onClick={onRedo}
              disabled={!canRedo}
              title="Redo"
            >
              <Redo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              className="w-[50px] rounded-none"
              size="icon"
              onClick={onClear}
              disabled={!hasWaypoints}
              title="Clear all points"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              className="w-[50px] rounded-none"
              size="icon"
              onClick={onReverse}
              disabled={!hasRoute}
              title="Reverse route"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </Button>
            <Button
              className="w-[50px] rounded-none"
              size="icon"
              onClick={onExportGPX}
              disabled={!hasRoute}
              title="Save route"
            >
              <Download className="h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
